import { Injectable } from '@angular/core';

import { Subject, firstValueFrom } from 'rxjs';
import { UserAvailabilityData, UserData, UserDataRole, UserProfileData } from './entourance-data.service';
import { LocalizationService } from './localization.service';
import { PersistenceService } from './persistence.service';
import { TimetableData, TimetableDataList } from '../models/timetable.model';
import { EnvironmentService } from './environment.service';
import { PGUtilities } from '../utilities/pg-utilities';
import { TravelDataService } from './travel-data.service';
import { TravelUser } from '../models/travel.model';

export class AuthUser {
    id:string = null;
    name:string = null;
    surname:string = null;

    availableRoles:Array<UserDataRole> = null;
    role:UserDataRole = null;
    superadmin:boolean = null;
    email:string = null;
    telephone:string = null;
    codice_fiscale:string = null;

    privacy_policy:0|1 = null;
    terms_and_conditions:0|1 = null;

    stripeId:string = null;

    language:string = null;

    groups:Array<{
        id: string,
        label: string
    }> = [];

    hasAcceptedPrivacyPolicy() {
        return this.privacy_policy == 1;
    }

    hasAcceptedTermsAndConditions() {
        return this.terms_and_conditions == 1;
    }

    getCompleteName() {
        let _result = '';

        for(let _prop of ['name','surname']) {
            let _value = this[_prop];

            if(_value) {
                if(_result != '') _result += ' '
                _result += _value
            }
        }

        return _result
    }

    constructor(fromData?:any) {
        if(fromData != null) {
            this.id = fromData.id;
            this.name = fromData.name;
            this.surname = fromData.surname;
            this.superadmin = fromData.superadmin == 1;
            this.email = fromData.email;
            this.telephone = fromData.telephone;
            this.codice_fiscale = fromData.codice_fiscale;

            if(this.name == this.email || this.name == this.telephone) this.name = null;

            this.language = fromData.language;

            this.privacy_policy = fromData.privacy_policy;
            this.terms_and_conditions = fromData.terms_and_conditions;

            this.stripeId = fromData.stripe_id

            this.groups = fromData.related_group || [];

            this.groups.sort((a, b) => {
                if(a.id < b.id) return -1;
                else if(a.id > b.id) return 1;
                else return 0;
            })

            this.availableRoles = [];
            this.role = null;

            if(fromData.related_role != null) {
                for(let _role of fromData.related_role) {
                    if(_role.name == 'base' && (_role.pivot == null || _role.pivot.realm_id == null)) {
                        this.availableRoles.push(_role)
                    }
                }

                if(fromData.current_role != null) {
                    for(let _cRole of this.availableRoles) {
                        if(_cRole.id == fromData.current_role) {
                            this.role = _cRole
                            break;
                        }
                    }
                }
            }
        }
    }
}

export class AuthAvailability {
    slots:TimetableDataList = new TimetableDataList();
    channels:Array<string> = [];

    constructor(fromData?:UserAvailabilityData) {
        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    if(i == 'slots') {
                        this[i] = new TimetableDataList(fromData[i])
                    }
                    else {
                        this[i] = PGUtilities.tryParseJSON(fromData[i])
                    }
                }
            }
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {    
    constructor(private environmentService:EnvironmentService, private persistenceService:PersistenceService, private localizationService:LocalizationService, private travelDataService:TravelDataService) {}

    statusChange = new Subject<void>();
    userChange = new Subject<void>();
    profileChange = new Subject<void>();
    availabilityChange = new Subject<void>();

    afterLogin = new Subject<void>();
    beforeLogout = new Subject<void>();

    user:AuthUser = null;
    availability:AuthAvailability = null;
    profile:UserProfileData = null;

    isLoading = false;

    private async _handleStatusChange() {
        if(this.user != null) {
            let _setLanguage = null;

            if(this.user.language != null) {
                if(this.localizationService.currentLanguage != this.user.language) {
                    _setLanguage = this.user.language;
                }            
            }
            else {
                try {
                    await this._saveUserLanguage()
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService._saveUserLanguage error')
                }
            }

            if(!this.user.superadmin && this.localizationService.disableTranslations) {
                try {
                    this.localizationService.setDisableTranslations(false, _setLanguage != null)
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService.setDisableTranslations error')
                }
            }

            if(_setLanguage != null) {
                try {
                    await this.localizationService.setLanguage(_setLanguage)
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService.setLanguage error')
                }
            }

            this.isLoading = false;
            this.statusChange.next()
            this.afterLogin.next();
        }
        else {
            if(this.localizationService.disableTranslations) this.localizationService.setDisableTranslations(false)
            this.isLoading = false;
            this.statusChange.next()
        }
    }

    private _saveUserLanguage() {
        return new Promise<void>((resolve) => {
            if(this.user != null) {
                this.user.language = this.localizationService.currentLanguage;
            }
            // Language persistence for travel users is local-only for now.
            resolve();
        })
    }

    private _applyTravelUser(travelUser: TravelUser) {
        this.user = new AuthUser({
            id: String(travelUser.id),
            name: travelUser.name,
            surname: null,
            email: travelUser.email,
            telephone: travelUser.phone || null,
            superadmin: 0,
            privacy_policy: 1,
            terms_and_conditions: 1,
            related_role: [],
            related_group: [],
            current_role: null,
            language: null,
            stripe_id: null,
            codice_fiscale: null,
        });
        this.availability = null;
        this.profile = {
            id: null,
            user_id: this.user.id,
            languages: this.environmentService.environment.AvailableApplicationLanguages?.[0] || 'it_IT',
            customer_care: false,
            company: null,
            vat_number: null,
            notification_settings: null,
        };
    }

    isLoggedIn(justUser?:boolean) {
        if(this.travelDataService.isEnabled()) {
            return !this.isLoading && this.travelDataService.isLoggedIn() && this.user != null;
        }

        if(this.isLoading || this.user == null) return false;
        else {
            if(justUser) return true
            else {
                return this.user.hasAcceptedPrivacyPolicy() && this.user.hasAcceptedTermsAndConditions();
            }
        }
    }

    /** True when tourist auth is on TravelAPIUrl (dev.travel). */
    usesTravelAuth() {
        return this.travelDataService.isEnabled();
    }

    accessMode:'business'|'editor' = null;

    init(mode?:'business'|'editor'):Promise<any> {
        return new Promise<void>((resolve) => {
            this.accessMode = mode;

            this.localizationService.currentLanguageChange.subscribe(() => {
                if(this.user != null) {
                    this._saveUserLanguage();
                }
            })

            if(!this.travelDataService.isEnabled()) {
                console.error('AuthService.init: TravelAPIUrl missing — set environments/environment.json TravelAPIUrl');
                this._handleStatusChange().then(() => resolve());
                return;
            }

            this.travelDataService.restoreSession().subscribe({
                next: (user) => {
                    if(user) {
                        this._applyTravelUser(user);
                    }
                    this._handleStatusChange().then(() => resolve());
                },
                error: () => {
                    this._handleStatusChange().then(() => resolve());
                }
            });
        })
    }

    requestOTP(contact:string, type:'email'|'phone') {
        if(!this.travelDataService.isEnabled()) {
            return Promise.reject(new Error('TravelAPIUrl not configured'));
        }
        return firstValueFrom(this.travelDataService.requestOtp(contact, type)).then(() => undefined);
    }

    loginOTP(contact:string, type:'email'|'phone', password:string) {
        if(!this.travelDataService.isEnabled()) {
            return Promise.reject(new Error('TravelAPIUrl not configured'));
        }

        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            const body = type === 'email'
                ? { email: contact, code: password }
                : { phone: contact, code: password };

            console.log('AuthService.loginOTP → Travel API', this.travelDataService.baseUrl);
            this.travelDataService.loginOtp(body).subscribe({
                next: (data) => {
                    this._applyTravelUser(data.user);
                    this._handleStatusChange().then(() => resolve());
                },
                error: (err) => {
                    console.warn('Travel OTP login failed', err);
                    this.isLoading = false;
                    reject();
                }
            });
        });
    }

    loginCode(_code:string) {
        return Promise.reject(new Error('Entourance code login is not available'));
    }

    loginToken(_token:string) {
        return Promise.reject(new Error('Entourance token login is not available'));
    }
    
    login(email:string, password:string): Promise<any> {
        // Tourist app: login always goes to TravelAPIUrl when configured
        if(this.travelDataService.isEnabled()) {
            return new Promise<void>((resolve, reject) => {
                this.isLoading = true;
                console.log('AuthService.login → Travel API', this.travelDataService.baseUrl);
                this.travelDataService.login(email, password).subscribe({
                    next: (data) => {
                        this._applyTravelUser(data.user);
                        this._handleStatusChange().then(() => resolve());
                    },
                    error: (err) => {
                        console.warn('Travel login failed', err);
                        this.isLoading = false;
                        reject();
                    }
                });
            });
        }

        console.error('AuthService.login: TravelAPIUrl missing — set environments/environment.json TravelAPIUrl to https://dev.travel.entourance.com/api');
        return Promise.reject(new Error('TravelAPIUrl not configured'));
    }

    accept(privacyPolicy:boolean, termsAndConditions:boolean): Promise<any> {
        // Travel users are treated as already accepted; keep local flags only.
        if(this.user != null) {
            this.user.privacy_policy = privacyPolicy ? 1 : 0;
            this.user.terms_and_conditions = termsAndConditions ? 1 : 0;
        }
        return this._handleStatusChange();
    }

    logout(): Promise<any> {
        return new Promise<void>((resolve) => {
            this.beforeLogout.next();
            if(!this.travelDataService.isEnabled()) {
                this.user = null;
                this.availability = null;
                this.profile = null;
                this.clearAuthToken();
                this._handleStatusChange().then(() => resolve());
                return;
            }

            this.travelDataService.logout().subscribe({
                next: () => {
                    this.user = null;
                    this.availability = null;
                    this.profile = null;
                    this._handleStatusChange().then(() => resolve());
                },
                error: () => {
                    this.user = null;
                    this.availability = null;
                    this.profile = null;
                    this.travelDataService.clearSession();
                    this._handleStatusChange().then(() => resolve());
                }
            });
        });
    }

    clearAuthToken() {
        this.persistenceService.removeItem('POIGEST_LoginMode');
        this.persistenceService.removeItem('POIGEST_AuthToken');
        this.persistenceService.removeItem('POIGEST_RefreshToken');
        this.travelDataService.clearSession();
    }

    hasAuthToken() {
        return !!this.travelDataService.token;
    }

    async getAuthToken(_ignoreLock?:boolean) {
        return this.travelDataService.token || null;
    }

    async tryRefreshToken() {
        // Travel Sanctum tokens are not refreshable — drop session on 401
        console.log('Travel auth: no refresh, clearing session');
        this.user = null;
        this.availability = null;
        this.profile = null;
        this.travelDataService.clearSession();
        this.clearAuthToken();
        await this._handleStatusChange();
    }

    isSuperAdmin():boolean {
        return this.isLoggedIn(true) && this.user.superadmin;
    }

    private _checkSetHighestRole() {
        return Promise.resolve();
    }

    hasAccessModeRole() {
        if(this.accessMode == null || this.user?.role?.name == this.accessMode) return true;
        else return false;
    }

    setUserRole(_id:string) {
        return Promise.resolve();
    }

    changePassword(_password:string) {
        return Promise.reject(new Error('Password change is not available via Entourance'));
    }

    setUserAvailabilitySlots(_slots: Array<TimetableData>) {
        return Promise.reject(new Error('Availability is not available on travel auth'));
    }

    setUserAvailabilityChannels(_channels:string) {
        return Promise.reject(new Error('Availability is not available on travel auth'));
    }

    setUserData(user:Partial<UserData>) {
        return new Promise<UserData>((resolve, reject) => {
            if(!this.isLoggedIn() || this.user == null) {
                reject();
                return;
            }
            for(let i in user) {
                this.user[i] = user[i];
            }
            this.profileChange.next();
            resolve(this.user as any);
        })
    }

    setUserProfile(profile:Partial<UserProfileData>) {
        return new Promise<UserProfileData>((resolve, reject) => {
            if(!this.isLoggedIn() || this.profile == null) {
                reject();
                return;
            }
            for(let i in profile) {
                this.profile[i] = profile[i];
            }
            this.profileChange.next();
            resolve(this.profile);
        })
    }

    isUserProfileIncomplete() {
        return this.isLoggedIn(true) && this.user != null && this.profile != null && (
            (
                (this.user.email == null || this.user.email == '') &&
                (this.user.telephone == null || this.user.telephone == '')
            ) ||
            this.user.name == null || this.user.name == '' || 
            this.user.surname == null || this.user.surname == '')
    }

    isBusinessProfileIncomplete() {
        return this.isLoggedIn(true) && this.user != null && this.profile != null && (
            (
                (this.user.email == null || this.user.email == '') &&
                (this.user.telephone == null || this.user.telephone == '')
            ) ||
            this.user.name == null || this.user.name == '' || 
            this.user.surname == null || this.user.surname == '' || 
            this.profile?.company == null || this.profile.company == '' ||
            this.profile.vat_number == null || this.profile.vat_number == '')
    }

}
